import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import API from "../constants/API";
import { getTeamID, JSONParser } from "../common/function";
import { authHeader } from "../common/mainfunction";
import { GET_FEATURE_FLAGS_URL } from "../common/url";
import { FeatureFlags } from "../types/api";

export interface FeatureFlagContextValue extends FeatureFlags {
  refreshFlags: () => Promise<void>;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | Record<string, never>>({});

// Default feature flags (used as fallback if API fails)
const DEFAULT_FLAGS: FeatureFlags = {
  ebsSyncEnabled: true,
  ebsOrgId: null,
  supplierInviteDisabled: true,
  manualSupplierCreationDisabled: true,
  emailImportEnabled: false,
  physicalScannerEnabled: false,
  enableWorkflowRouting: false,
  supplierPortalEnabled: false,
  signupEnabled: false,
  mobileBlockerEnabled: true,
  approvalLimitsEnabled: false,
  ssoEnabled: false,
  jitAccessEnabled: false,
  oicSupplierSyncEnabled: false,
  oicExportEnabled: false,
  oicValidationEnabled: false,
  abnLookupEnabled: false,
  supplierDashboardEnabled: true,
  defaultCurrency: "USD",
  yardiExportEnabled: false,
  achPaymentEnabled: false,
  supplierDocumentsEnabled: false,
  barcodeSeparationEnabled: false,
  teamsIntegrationEnabled: false,
  slackIntegrationEnabled: false,
  i18nEnabled: false,
  defaultLanguage: "en",
  availableLanguages: ["en"],
  glSegmentsEnabled: false,
  budgetHolderRoutingEnabled: false,
  taxRegionEnabled: false,
  headerLineVarianceCheckEnabled: false,
  paymentTermsConversionEnabled: false,
  expenseModuleEnabled: false,
  // Auto due-date default: due date = invoice date + N days (see design doc)
  autoDueDateEnabled: false,
  autoDueDateOffsetDays: 30,
};

interface FeatureFlagProviderProps {
  children: React.ReactNode;
}

/**
 * Feature Flag Provider
 * Fetches feature flags from API on mount and provides them to children
 * Prevents flash by not rendering children until flags are loaded
 */
export const FeatureFlagProvider = ({ children }: FeatureFlagProviderProps) => {
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchFeatureFlags = useCallback(async () => {
    try {
      const teamId = getTeamID();
      if (!teamId) {
        console.warn("[FeatureFlags] No teamId found, using defaults");
        setIsLoading(false);
        return;
      }

      const config = {
        method: "GET",
        url: GET_FEATURE_FLAGS_URL,
        headers: authHeader(),
        params: { teamId },
      };

      const response = await API(config);
      const decrypted = response.data;
      const parsed = JSONParser(decrypted, { status: null, data: null });

      if (parsed.status === "Success" && parsed.data) {
        const flagData = parsed.data as Partial<FeatureFlags>;
        setFlags({
          ...DEFAULT_FLAGS,
          ...flagData,
        });
      } else {
        console.warn("[FeatureFlags] API returned non-success, using defaults");
      }
    } catch (error) {
      console.error("[FeatureFlags] Error fetching feature flags:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeatureFlags();
  }, [fetchFeatureFlags]);

  // Don't render children until flags are loaded
  // This prevents the "flash" of default UI before feature flags apply
  if (isLoading) return null;

  return (
    <FeatureFlagContext.Provider value={{ ...flags, refreshFlags: fetchFeatureFlags }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

/**
 * Hook to access feature flags
 * @returns Feature flags object
 *
 * Usage:
 *   const { physicalScannerEnabled, emailImportEnabled } = useFeatureFlags();
 *
 *   {physicalScannerEnabled && <ScanButton />}
 */
export const useFeatureFlags = (): FeatureFlagContextValue => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error("useFeatureFlags must be used within a FeatureFlagProvider");
  }
  return context as FeatureFlagContextValue;
};

export default FeatureFlagContext;
